import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionLink, Chips, NumberInput } from '@cian/ui-kit';
import { debounce } from '@cian/newbuilding-utils';

import { priceInputDescriptors, priceTypeDescriptors } from '../../constants/descriptors';
import { isDescriptorActive } from '../../utils/filters';
import { IApplicationState } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersData } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';
import { FILTER_SELECTION_TIMEOUT } from '../../constants/timeouts';
import { QuickFilterPriceWrapper } from '../../components/Filters/QuickFilterPriceWrapper';
import { Adornment } from '../../components/Adornment';

export const QuickPriceFilter: React.FC = React.memo(() => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);

  const applyFilter = React.useCallback(() => {
    dispatch(getOffersData({ reason: 'quick_price_filter' }));
  }, [dispatch]);

  const applyFilterDebounced = React.useRef(debounce(FILTER_SELECTION_TIMEOUT, applyFilter));

  React.useEffect(() => {
    applyFilterDebounced.current = debounce(FILTER_SELECTION_TIMEOUT, applyFilter);
  }, [applyFilter]);

  // Получаем значения цены
  const priceGte = jsonQuery.price?.value?.gte ?? '';
  const priceLte = jsonQuery.price?.value?.lte ?? '';

  // Получаем активный тип цены
  const activePriceType = React.useMemo(() => {
    const activeDescriptor = priceTypeDescriptors.find(descriptor => isDescriptorActive(descriptor)(jsonQuery));

    /* istanbul ignore next */
    return activeDescriptor ? activeDescriptor.state : 'price_sm_all';
  }, [jsonQuery]);

  // Опции для Chips
  const priceTypeOptions = React.useMemo(
    () =>
      priceTypeDescriptors
        .map(({ state, label }) => ({
          label,
          value: state,
        }))
        .reverse(),
    [],
  );

  // Обработчик изменения цены
  const handlePriceChange = React.useCallback(
    (type: 'gte' | 'lte') => (value: number | undefined) => {
      const descriptor = priceInputDescriptors[0];
      dispatch({
        type: `${descriptor.name}/${descriptor.state}`,
        subType: type,
        value,
      });
      dispatch(updateTags());
      applyFilterDebounced.current();
    },
    [dispatch],
  );

  // Обработчик изменения типа цены
  const handlePriceTypeClick = React.useCallback(
    (value: string) => {
      const descriptor = priceTypeDescriptors.find(d => d.state === value);

      /* istanbul ignore else */
      if (descriptor) {
        dispatch({
          type: `${descriptor.name}/${descriptor.state}`,
          value,
        });
        dispatch(updateTags());
        applyFilterDebounced.current();
      }
    },
    [dispatch],
  );

  // Обработчик сброса фильтров
  const handleReset = React.useCallback(() => {
    // Сброс значений цены
    const priceDescriptor = priceInputDescriptors[0];
    dispatch({
      type: `${priceDescriptor.name}/${priceDescriptor.state}`,
      subType: 'gte',
      value: undefined,
    });
    dispatch({
      type: `${priceDescriptor.name}/${priceDescriptor.state}`,
      subType: 'lte',
      value: undefined,
    });

    // Сброс типа цены на "за все"
    const defaultPriceType = priceTypeDescriptors.find(d => d.state === 'price_sm_all');
    if (defaultPriceType) {
      dispatch({
        type: `${defaultPriceType.name}/${defaultPriceType.state}`,
        value: defaultPriceType.state,
      });
    }

    dispatch(updateTags());
    applyFilterDebounced.current();
  }, [dispatch]);

  return (
    <QuickFilterPriceWrapper
      resetButton={
        <ActionLink onClick={handleReset} fontWeight="bold">
          Сбросить
        </ActionLink>
      }
    >
      <div>
        <NumberInput
          type="number"
          size="M"
          aria-label="Минимальная цена квартиры"
          value={priceGte ? Number(priceGte) : undefined}
          onChange={value => handlePriceChange('gte')(value)}
          leftAdornment={<Adornment>от</Adornment>}
          rightAdornment={<Adornment>₽</Adornment>}
        />
      </div>
      <div>
        <NumberInput
          type="number"
          size="M"
          aria-label="Максимальная цена квартиры"
          value={priceLte ? Number(priceLte) : undefined}
          onChange={value => handlePriceChange('lte')(value)}
          leftAdornment={<Adornment>до</Adornment>}
          rightAdornment={<Adornment>₽</Adornment>}
        />
      </div>
      <Chips
        options={priceTypeOptions}
        size="M"
        value={[activePriceType]}
        selectType="single"
        onChipClick={handlePriceTypeClick}
      />
    </QuickFilterPriceWrapper>
  );
});

QuickPriceFilter.displayName = 'QuickPriceFilter';
